import {
    Address,
    BasisType,
    BookableSchedules,
    BookingflowStateMachine,
    GeneralActions,
    IPreviousBooking,
} from '@raskrask-dk/bookingflow-state-machine'
import { MoveBookingDependencies } from '@raskrask-dk/bookingflow-state-machine/lib/states/moveBookingState/moveBookingState.types'
import { RRDate } from '@raskrask-dk/raskrask-utils'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { BfsmRepoAdaptors } from '../../adaptors/bfsmRepoAdaptors'
import { BasisTypeBF, GoalBF, PartnerBF, ServiceBF } from '../../models'
import { useAppDispatch } from '../../redux/hooks'
import { setErrorcase, setIsLoading } from '../../redux/reducers/appSettings'

export const useLoadData = (
    bookingId: string,
    BFSM: BookingflowStateMachine,
    setDependencies: (data: MoveBookingDependencies | undefined) => void,
    numberOfDaysLoaded: number,
    loadingStartDate: RRDate,
    flowType: 'CancelBooking' | 'MoveBooking' | 'Booking'
): IPreviousBooking | undefined => {
    const [oldBooking, setOldBooking] = useState<IPreviousBooking>()
    const dispatch = useAppDispatch()
    useEffect(() => {
        const lookUpData = async () => {
            try {
                dispatch(setIsLoading(true))
                const previousBooking = await BFSM.repo.getBookingData(
                    bookingId
                )
                if (!previousBooking || previousBooking.address === 'INVALID')
                    throw new Error('MoveBookBadData')
                const address = new Address(
                    previousBooking.address.address,
                    previousBooking.address.geoLat,
                    previousBooking.address.geoLng,
                    previousBooking.address.zipCode,
                    '0'
                )
                const basisType = new BasisType(
                    previousBooking.basisName,
                    previousBooking.basisTypeID
                )
                const customerType =
                    previousBooking.goalID === 23 ? 'Company' : 'Private'

                const endDate = new RRDate(
                    loadingStartDate.toString('yyyy-MM-dd HH:mm'),
                    'yyyy-MM-dd HH:mm'
                ).plus({ day: numberOfDaysLoaded })
                const bookingflowDTO: BookableSchedules =
                    BfsmRepoAdaptors.toBookableSchedulesDTO(
                        address,
                        basisType as BasisTypeBF,
                        customerType,
                        loadingStartDate,
                        endDate,
                        { profileIds: [previousBooking.profileID] }
                    )
                const bfModels = await BFSM.service.loadSchedules(
                    bookingflowDTO
                )
                // This IF is here for when no data can be fetched for the cancelbooking case.
                // This is to not display the date carousel.
                if (bfModels.goals.length < 1 && flowType === 'CancelBooking') {
                    dispatch(setIsLoading(false))
                    return
                }
                const goal = bfModels.goals.find(
                    (g) => g.id === previousBooking.goalID
                )
                if (goal === undefined)
                    throw new Error('MoveBookNoAvailabilities')

                const service = (goal as GoalBF).services.find(
                    (s) => s.id == previousBooking.serviceTypeID
                )

                if (service === undefined)
                    throw new Error('MoveBookNoAvailabilities')

                const partner = (service as ServiceBF)
                    .getPartnersBy()
                    .find((p) => p.id == previousBooking.profileID)

                if (partner === undefined && flowType !== 'CancelBooking')
                    throw new Error('MoveBookNoAvailabilities')

                setOldBooking(previousBooking)
                setDependencies({
                    address: address,
                    basisType: basisType,
                    customerType: customerType,
                    goal: goal as GoalBF,
                    service: service as ServiceBF,
                    partner: partner as PartnerBF,
                })
                dispatch(setIsLoading(false))
            } catch (err) {
                if ((err as Error).message === 'MoveBookBadData')
                    dispatch(setErrorcase('MoveBookBadData'))
                else if ((err as Error).message === 'MoveBookNoAvailabilities')
                    dispatch(setErrorcase('MoveBookNoAvailabilities'))
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            }
        }
        lookUpData()
    }, [])

    return oldBooking
}

export const moveBookLoadMoreDays = async (
    dayNumber: number,
    dependencies: MoveBookingDependencies | undefined,
    carouselDates: CarouselDates,
    BFSM: BookingflowStateMachine,
    setDependencies: (data: MoveBookingDependencies | undefined) => void,
    setNumberOfDaysLoaded: Dispatch<SetStateAction<number>>,
    numberOfDaysLoaded: number
): Promise<void> => {
    if (dependencies !== undefined) {
        const bfModels = await BFSM.service.loadMore(
            dayNumber,
            BFSM.getData().basisType
        )
        const goal = bfModels.goals.find((g) => g.id === dependencies.goal.id)
        if (goal === undefined)
            throw new Error(
                'Something went wrong... We could not get the data of your booking.'
            )
        const service = goal.services.find(
            (s) => s.id == dependencies.service.id
        )
        if (service === undefined)
            throw new Error(
                'Something went wrong... We could not get the data of your booking.'
            )
        const partner = service
            .getPartnersBy()
            .find((p) => p.id == dependencies.partner?.id)
        if (partner === undefined)
            throw new Error(
                'Something went wrong... We could not get the data of your booking.'
            )

        setDependencies({
            ...dependencies,
            goal: goal,
            service: service,
            partner: partner,
        })

        setNumberOfDaysLoaded(numberOfDaysLoaded + dayNumber)
    }
}
